import React from 'react';

import { Inspector } from '@quintype/em/components/inspector';
import { CreatableSelect } from '@quintype/em/components/select';
import { get, startCase } from 'lodash-es';
import { Checkbox } from '@quintype/em/components/checkbox';
import validate from 'validate.js';
import { Radio, RadioOption } from '@quintype/em/components/radio-button';
import { DateRangePicker } from '@quintype/em/components/date-range-picker';
import { ErrorMessage } from '@quintype/em/components/error-message';
import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import parseISO from 'date-fns/parseISO';
import set from 'date-fns/set';
import addDays from 'date-fns/addDays';

import './subscription_filter.scss';

import { getSubscriptionGroupsLite } from '../../api';
import endOfDay from 'date-fns/endOfDay';
import SliderCheckbox from '../library-components/SliderCheckbox/SliderCheckbox';

const allOption = [{ label: 'All', value: 'all' }];

export function customValidation ({ endTimeStamp }) {
  // startTimeStamp from date picker is date instance with time as (12:00 am)
  // so we are formatting the current date with 12:00 am time
  const todayDate = set(new Date(), {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0
  });

  const tommorrowTimeStamp = getUnixTime(addDays(todayDate, 1));

  if (endTimeStamp > tommorrowTimeStamp) {
    return {
      message: 'Future date is not allowed',
      type: 'to'
    };
  }
}

export default class SubscriptionFilter extends React.Component {
  constructor (props) {
    super(props);
    const renewalExpiryDate = this.props.filters['renewal-expiry-date'];
    const renewalExpiryType = this.props.filters['renewal-expiry-type'] || 'renewal';
    this.state = {
      group: '',
      plans: [],
      errors: [],
      status: this.props.filters.status,
      cancelled: this.props.filters.cancelled,
      subscriptionType: this.props.filters.subscription_type,
      subscriptionGroupType: this.props.filters.subscription_group_type,
      couponValidityEnabled: !!this.props.filters['sub-date'].start,
      isDatePopupOpen: false,
      subscriptionGroups: null,
      subscriptionGroupsOptions: null,
      timeStamp: {
        start: this.props.filters['sub-date'].start,
        end: this.props.filters['sub-date'].end
      },
      renewalExpiryEnabled: !!(renewalExpiryDate && renewalExpiryDate.start),
      renewalExpiryType,
      renewalExpiryTimeStamp: {
        start: (renewalExpiryDate && renewalExpiryDate.start) || null,
        end: (renewalExpiryDate && renewalExpiryDate.end) || null
      },
      isRenewalExpiryDatePopupOpen: false
    };
  }

  componentDidMount () {
    this.loadSubscriptionGroups();
  }

  loadSubscriptionGroups (groupTypes = this.state.subscriptionGroupType) {
    const subscriptionGroupType = (groupTypes && groupTypes.length) ? groupTypes : 'all';
    getSubscriptionGroupsLite(subscriptionGroupType, true).then((subscriptionGroups) => {
      const [group, plans] = this.getInitialGroupAndPlan(subscriptionGroups);
      this.setState({
        subscriptionGroups,
        subscriptionGroupsOptions: this.makeOptions(subscriptionGroups),
        group,
        plans
      });
    });
  }

  getInitialGroupAndPlan (subscriptionGroups) {
    if (this.props.filters.group === 'all') {
      return [...allOption, allOption];
    }
    const selectedGroup = subscriptionGroups.filter(
      (group) => group.id.toString() === this.props.filters.group
    );

    if (!selectedGroup.length || !selectedGroup[0]) {
      return [...allOption, allOption];
    }

    const selectedPlans = selectedGroup[0][
      'subscription_plans'
    ].filter((plan) => this.props.filters.plans.includes(plan.id.toString()));
    const group = this.makeOptions(selectedGroup, false);
    const plans = this.makeOptions(selectedPlans, false);
    return [...group, plans];
  }

  makeOptions (array, showAllOption = true) {
    const options = array.map((item) => ({
      label: item.name || item.title,
      value: item.id.toString()
    }));
    return showAllOption ? allOption.concat(options) : options;
  }

  planOptions () {
    if (this.state.group.value === 'all') {
      return allOption;
    }
    const selectedGroup = this.state.subscriptionGroups.filter(
      (group) => group.id.toString() === this.state.group.value
    )[0];
    return this.makeOptions(selectedGroup['subscription_plans'], false);
  }

  updateFilter (field, value) {
    if (field === 'subscriptionType') {
      const subscriptionGroupType = value === 'Recurring' ? ['standard'] : ['all'];
      this.setState(
        { subscriptionGroupType: subscriptionGroupType, subscriptionType: value });
      this.loadSubscriptionGroups(subscriptionGroupType);
      return;
    }

    if (field === 'group') {
      this.setState({ group: value, plans: [] });
      return;
    }

    if (field === 'renewalExpiryType') {
      // When renewal/expiry type changes, update status accordingly
      if (this.state.renewalExpiryEnabled) {
        if (value === 'expiry') {
          // Set status to expired and disable all status checkboxes
          this.setState({
            renewalExpiryType: value,
            status: ['expired']
          });
        } else if (value === 'renewal') {
          // Set status to all and enable status checkboxes
          this.setState({
            renewalExpiryType: value,
            status: ['all']
          });
        }
      } else {
        this.setState({ renewalExpiryType: value });
      }
      return;
    }

    this.setState({ [field]: value });
  }

  updateStatus (field, value) {
    const statusArr = this.state.status;
    if (value) {
      this.setState({
        status: [...this.state.status, field]
      });
    } else {
      var index = statusArr.indexOf(field);
      if (index > -1) {
        const newArr = statusArr.filter(status => status !== field);
        this.setState({
          status: newArr
        });
      }
    }
  }

  updateSubscriptionGroupType (field, value) {
    const currentGroupTypes = this.state.subscriptionGroupType;
    const updatedGroupTypes = value
      ? [...currentGroupTypes, field]
      : currentGroupTypes.filter(groupType => groupType !== field);

    this.setState({ subscriptionGroupType: updatedGroupTypes });
    this.loadSubscriptionGroups(updatedGroupTypes);
  }

  timeStampConditions () {
    const { couponValidityEnabled } = this.state;

    return couponValidityEnabled
      ? {
        start_timestamp: {
          presence: true
        },
        end_timestamp: {
          presence: true
        }
      }
      : {};
  }

  renewalExpiryTimeStampConditions () {
    return get(this.state, 'renewalExpiryEnabled')
      ? {
        renewal_expiry_start_timestamp: {
          presence: true
        },
        renewal_expiry_end_timestamp: {
          presence: true
        }
      }
      : {};
  }

  constraints () {
    return Object.assign({}, this.timeStampConditions(), this.renewalExpiryTimeStampConditions());
  }

  applyFilters () {
    const currentDates = {
      start_timestamp: this.state.timeStamp.start,
      end_timestamp: this.state.timeStamp.end,
      renewal_expiry_start_timestamp: this.state.renewalExpiryTimeStamp.start,
      renewal_expiry_end_timestamp: this.state.renewalExpiryTimeStamp.end
    };

    const validatedData = validate(currentDates, this.constraints());
    if (validatedData) {
      this.setState({ errors: validatedData });
    } else {
      const filters = {
        group: this.state.group.value,
        plans: this.state.plans.length
          ? this.state.plans.map((plan) => plan.value)
          : ['all'],
        status: this.state.status,
        cancelled: this.state.cancelled,
        subscription_type: this.state.subscriptionType,
        subscription_group_type: this.state.subscriptionGroupType,
        'sub-date': {
          start: this.state.timeStamp.start,
          end: this.state.timeStamp.end
        }
      };

      if (this.state.renewalExpiryEnabled) {
        filters['renewal-expiry-date'] = {
          start: this.state.renewalExpiryTimeStamp.start,
          end: this.state.renewalExpiryTimeStamp.end
        };
        filters['renewal-expiry-type'] = this.state.renewalExpiryType;
      }

      this.props.onApplyFilters(filters);
    }
  }

  render () {
    const {
      couponValidityEnabled,
      errors,
      isDatePopupOpen,
      timeStamp: { start, end },
      renewalExpiryEnabled,
      renewalExpiryType,
      renewalExpiryTimeStamp: { start: renewalExpiryStart, end: renewalExpiryEnd },
      isRenewalExpiryDatePopupOpen
    } = this.state;
    const dateRangePickerProps =
      start && end
        ? {
          startTimeStamp: getUnixTime(parseISO(start)),
          endTimeStamp: getUnixTime(parseISO(end))
        }
        : {};

    const renewalExpiryDateRangePickerProps =
      renewalExpiryStart && renewalExpiryEnd
        ? {
          startTimeStamp: getUnixTime(parseISO(renewalExpiryStart)),
          endTimeStamp: getUnixTime(parseISO(renewalExpiryEnd))
        }
        : {};

    const isTimestampNotSelected = get(errors, ['start_timestamp', 0], false);
    const isRenewalExpiryTimestampNotSelected = get(errors, ['renewal_expiry_start_timestamp', 0], false);
    return (
      <div className="sub-filter-container">
        <Inspector
          title="Filters"
          isActive
          onClose={this.props.onClose}
          actionButtonLabel="Apply filters"
          onActionButtonClick={() => this.applyFilters()}
        >
          <div className="sub-filter-radio">
            <Radio
              name="subscription-type"
              label="Subscription Type"
              selected={this.state.subscriptionType || false}
              onChange={(e) => this.updateFilter('subscriptionType', e.target.value)}
            >
              <RadioOption value="all">All</RadioOption>
              <RadioOption value="Recurring">Recurring</RadioOption>
              <RadioOption value="One Time">One Time</RadioOption>
            </Radio>
          </div>
          <span className="status-label">Subscription Group Type</span>
          <div className="status-list vertical">
            {
              this.props.subscriptionGroupTypeList.map((subscriptionGroupType, i) => {
                const isRecurringSelected = get(this.state, 'subscriptionType') === 'Recurring';
                return (
                  <div className="status-item" data-test-id={`filter-subscription-group-type-item-${subscriptionGroupType}`} key={i}>
                    <Checkbox
                      label={startCase(subscriptionGroupType)}
                      id={subscriptionGroupType}
                      onChange={(checked) => this.updateSubscriptionGroupType(subscriptionGroupType, checked)}
                      checked={get(this.state, 'subscriptionGroupType', []).includes(subscriptionGroupType)}
                      disabled={isRecurringSelected}
                    />
                  </div>
                );
              })}
          </div>
          <CreatableSelect
            label="Subscription groups"
            options={this.state.subscriptionGroupsOptions || []}
            value={this.state.group}
            onChange={(option) => this.updateFilter('group', option)}
            clearable={false}
            searchable={false}
          />
          <CreatableSelect
            label="Subscription plans"
            options={this.state.subscriptionGroups ? this.planOptions() : []}
            value={this.state.subscriptionGroups ? this.state.plans : null}
            helpText="All plans are selected by default."
            onChange={(value) => this.updateFilter('plans', value)}
            isMulti
            isClearable={false}
          />

          <div className="sub-filter-radio">
            <Radio
              name="cancelled"
              label="User Initiated Cancellation"
              selected={this.state.cancelled || false}
              onChange={(e) => this.updateFilter('cancelled', e.target.value)}
            >
              <RadioOption value="true">Yes</RadioOption>
              <RadioOption value="false">No</RadioOption>
              <RadioOption value="all">Both</RadioOption>
            </Radio>
          </div>
          <span className="status-label">Status</span>
          <div className="status-list vertical">
            {this.props.statusList.map((status, i) => {
              const isStatusDisabled = get(this.state, 'renewalExpiryEnabled') && get(this.state, 'renewalExpiryType') === 'expiry';
              return (
                <div className="status-item" data-test-id={`filter-status-item-${status}`} key={i}>
                  <Checkbox
                    label={startCase(status)}
                    id={status}
                    onChange={(checked) => this.updateStatus(status, checked)}
                    checked={get(this.state, 'status', []).includes(status)}
                    disabled={isStatusDisabled}
                  />
                </div>
              );
            })}
          </div>
          <div className="checkbox-container">
            <SliderCheckbox
              name="date-range-checkbox"
              label="Subscription Creation Range"
              onChange={(e) => {
                const checked = e.target.checked;
                this.setState({
                  couponValidityEnabled: checked
                });
                if (!checked) {
                  this.setState({
                    timeStamp: {
                      start: null,
                      end: null
                    }
                  });
                }
              }}
              enabled={couponValidityEnabled}
            />
          </div>
          {couponValidityEnabled && (
            <>
              <DateRangePicker
                {...dateRangePickerProps}
                dateLabel=""
                dateFieldClass={isTimestampNotSelected ? 'red-border' : ''}
                customValidation={customValidation}
                onDateRangeSelect={(start, end) => {
                  this.setState({
                    timeStamp: {
                      start: fromUnixTime(start).toISOString(),
                      end: endOfDay(fromUnixTime(end)).toISOString()
                    }
                  });
                }}
                onDatePopupOpenOrClose={(popupState) => {
                  if (popupState) {
                    this.setState({
                      isDatePopupOpen: true
                    });
                  } else {
                    this.setState({
                      isDatePopupOpen: false
                    });
                  }
                }}
              />
              <div className="error-message-container">
                <ErrorMessage
                  message={
                    isTimestampNotSelected ? 'Please select date range' : ''
                  }
                />
              </div>
              {/* This increases the height of parent container when date popup is open
               * as normal flow parent (not absolute/ fixed positioned) does not account for
               * height of absolutely/fixed positioned element (date popup). Although a fixed positioned
               * parent accounts height  for fixed positioned child, still we need some space below
               * This can be removed if there are enough items below the date range picker */}
              {isDatePopupOpen && (
                <div className="sub-coupon-parent-ht-increase"></div>
              )}
            </>
          )}
          <div className="checkbox-container">
            <SliderCheckbox
              name="renewal-expiry-range-checkbox"
              label="Subscription Renewal or Expiry Range"
              onChange={(e) => {
                const checked = e.target.checked;
                if (checked) {
                  // When enabled, set status based on renewalExpiryType
                  if (this.state.renewalExpiryType === 'expiry') {
                    this.setState({
                      renewalExpiryEnabled: checked,
                      status: ['expired']
                    });
                  } else {
                    this.setState({
                      renewalExpiryEnabled: checked,
                      status: ['all']
                    });
                  }
                } else {
                  // When disabled, reset status to all
                  this.setState({
                    renewalExpiryEnabled: checked,
                    renewalExpiryTimeStamp: {
                      start: null,
                      end: null
                    },
                    status: ['all']
                  });
                }
              }}
              enabled={renewalExpiryEnabled}
            />
          </div>
          {renewalExpiryEnabled && (
            <>
              <div className="renewal-expiry-radio-container">
                <Radio
                  name="renewal-expiry-type"
                  selected={renewalExpiryType}
                  onChange={(e) => this.updateFilter('renewalExpiryType', e.target.value)}
                >
                  <RadioOption value="renewal">Subscription Renewal</RadioOption>
                  <RadioOption value="expiry">Subscription Expiry</RadioOption>
                </Radio>
              </div>
              <DateRangePicker
                {...renewalExpiryDateRangePickerProps}
                dateLabel=""
                dateFieldClass={isRenewalExpiryTimestampNotSelected ? 'red-border' : ''}
                customValidation={customValidation}
                onDateRangeSelect={(start, end) => {
                  this.setState({
                    renewalExpiryTimeStamp: {
                      start: fromUnixTime(start).toISOString(),
                      end: endOfDay(fromUnixTime(end)).toISOString()
                    }
                  });
                }}
                onDatePopupOpenOrClose={(popupState) => {
                  if (popupState) {
                    this.setState({
                      isRenewalExpiryDatePopupOpen: true
                    });
                  } else {
                    this.setState({
                      isRenewalExpiryDatePopupOpen: false
                    });
                  }
                }}
              />
              <div className="error-message-container">
                <ErrorMessage
                  message={
                    isRenewalExpiryTimestampNotSelected ? 'Please select date range' : ''
                  }
                />
              </div>
              {isRenewalExpiryDatePopupOpen && (
                <div className="sub-coupon-parent-ht-increase"></div>
              )}
            </>
          )}
        </Inspector>
      </div>
    );
  }
}
